// 19章 内装工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_DRY_PERIOD = `
<svg viewBox="0 0 660 210" role="img" aria-label="床材を張る前の下地の乾燥期間。モルタル塗り下地は施工後14日以上、コンクリート下地は施工後28日以上放置して乾燥させてから床材を張る。水分が残ると膨れ・剥がれの原因になる">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="40" y="60" width="240" height="90" rx="10"/>
    <rect x="380" y="60" width="240" height="90" rx="10"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="160" y="95">モルタル塗り下地</text>
    <text x="500" y="95">コンクリート下地</text>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="160" y="128">14日以上</text>
    <text x="500" y="128">28日以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="45" font-weight="bold">床仕上げ前の下地の乾燥（19.2.3・19.4.3・19.5）</text>
    <text x="40" y="185">下地に水分が残ったまま張ると、膨れ・剥がれ・接着不良の原因。乾燥の確認が重要</text>
  </g>
</svg>`;

const SVG_SHEET = `
<svg viewBox="0 0 660 230" role="img" aria-label="ビニル床シートの張付けと熱溶接。接着剤をくし目ごてで塗りべた張りし、ローラーで圧着する。継目は硬化後に床厚の3分の2まで溝切りし、熱溶接機で床シートと溶接棒を溶かして余盛りができるまで溶接、冷却後に余盛りを削り平滑にする">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="120" width="540" height="30"/></g>
  <g fill="currentColor" opacity="0.6"><rect x="60" y="105" width="260" height="15"/><rect x="340" y="105" width="260" height="15"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M320 105 L330 120 L340 105"/>
  </g>
  <g fill="currentColor" opacity="0.85"><circle cx="330" cy="112" r="5"/></g>
  <g fill="currentColor" font-size="12.5">
    <text x="335" y="98">溶接棒</text>
    <text x="60" y="45" font-weight="bold">ビニル床シートの熱溶接工法（19.2.3）</text>
    <text x="60" y="70">① 接着剤をくし目ごてで塗りべた張り→ローラー圧着</text>
    <text x="60" y="92">② 硬化後、継目を床厚の2/3まで溝切り（V字/U字）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="60" y="180">③ 熱溶接機で床シートと溶接棒を同時に溶かし、余盛りができるまで加圧しながら溶接</text>
    <text x="60" y="202">④ 完全に冷却後、余盛りを削り取り平滑に（防水性のある一体の床になる）</text>
  </g>
</svg>`;

const SVG_CARPET = `
<svg viewBox="0 0 660 230" role="img" aria-label="カーペットの工法。グリッパー工法は周囲にグリッパーを固定し下敷き材を敷いてカーペットを引き伸ばして引っ掛ける。全面接着工法は接着剤で下地に張る。タイルカーペットは粘着はく離形接着剤で市松敷きにする">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30" y="55" width="290" height="140" rx="10"/>
    <rect x="340" y="55" width="290" height="140" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="175" y="80">グリッパー工法</text>
    <text x="485" y="80">全面接着工法</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="46" y="106">・周囲にグリッパー（釘の付いた板）</text>
    <text x="46" y="128">・下敷き材を敷く（クッション）</text>
    <text x="46" y="150">・引き伸ばして端を引っ掛ける</text>
    <text x="46" y="176" opacity="0.8">織じゅうたん・タフテッド向け</text>
    <text x="356" y="106">・接着剤で下地に直接張る</text>
    <text x="356" y="128">・タイルカーペットは粘着はく離形</text>
    <text x="356" y="150">・市松敷き（目の向きを交互）</text>
    <text x="356" y="176" opacity="0.8">タイルカーペット・タフテッド向け</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="220">カーペットは消防法の防炎性能（防炎表示）が必要。タイルカーペットは1枚ずつ交換できる</text>
  </g>
</svg>`;

const SVG_GB_DIRECT = `
<svg viewBox="0 0 660 220" role="img" aria-label="せっこうボードの直張り工法。コンクリート下地に団子状の直張り用接着材を点々と塗り、ボードを押し付けて定規でたたきながら上下左右を調整して張る。接着材の盛上げ高さは仕上げ厚さの2倍以上">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="50" width="30" height="150"/></g>
  <g fill="currentColor" opacity="0.6">
    <ellipse cx="110" cy="75" rx="18" ry="12"/>
    <ellipse cx="110" cy="125" rx="18" ry="12"/>
    <ellipse cx="110" cy="175" rx="18" ry="12"/>
  </g>
  <g fill="currentColor" opacity="0.4"><rect x="128" y="45" width="14" height="160"/></g>
  <g fill="currentColor" font-size="12.5">
    <text x="55" y="35" opacity="0.85">コンクリート下地</text>
    <text x="160" y="80">直張り用接着材（団子状）</text>
    <text x="160" y="130">間隔：周辺150〜200mm</text>
    <text x="172" y="150" opacity="0.85">床上1.2m以下200〜250mm</text>
    <text x="172" y="170" opacity="0.85">床上1.2m超250〜300mm</text>
    <text x="148" y="45" opacity="0.85">せっこうボード</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="215">盛上げ高さは仕上げ厚さの2倍以上。定規でたたきながら上下左右を調整（GLボンド工法・19.7.3）</text>
  </g>
</svg>`;

const ch19: Record<string, Commentary> = {
  "19.1.1": {
    plainSummary:
      "この章は建築物の床・壁・天井を対象とする内装工事に適用する。1章とセットで適用する。",
    why: "内装は人が最も近くで触れる仕上げです。床材（ビニル・カーペット・塗床・フローリング・畳）、壁・天井のボード・壁紙、断熱・防露を扱います。共通する重要事項は「下地の乾燥」と「シックハウス対策（F☆☆☆☆）」です。",
  },

  "19.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②仕上り面が所要の状態 ③床は著しい不陸がなく床鳴りが生じない、断熱・防露は断熱性に影響する厚さの不ぞろい・欠け等の欠陥がないこと。",
    why: "床の「床鳴り」と「不陸」は内装クレームの二大巨頭。床鳴りは施工不良（接着・釘留め不足）、不陸は下地の平滑さ不足が原因です。",
  },

  "19.2.1": {
    plainSummary:
      "この節はビニル床シート・ビニル床タイル・ゴム床タイルを用いる床仕上げ工事に適用する。",
    glossary: [
      {
        term: "ビニル床シート",
        meaning:
          "塩化ビニル製の長尺の床材。継目を溶接すれば防水性のある一体の床になる。病院・厨房等に使う。",
      },
    ],
  },

  "19.2.2": {
    plainSummary:
      "ビニル床シートは特記がなければ厚2.0mm、ビニル床タイルは厚2.0mm。特殊機能床材（帯電防止・視覚障害者用・防滑性等）は特記。接着剤は施工箇所に応じた主成分の区分（水回りはエポキシ・ウレタン樹脂系）を使う（F☆☆☆☆）。",
    why: "接着剤の選定が重要。水や湿気を受ける場所（地下・厨房・浴室直上）は耐水性の高いエポキシ・ウレタン系、一般床は酢酸ビニル系などの汎用接着剤、と使い分けます。水回りに汎用接着剤を使うと剥がれます。",
  },

  "19.2.3": {
    plainSummary:
      "下地はモルタル塗りは施工後14日以上・コンクリートは28日以上放置して乾燥させる（傷はポリマーセメントで補修、突起はサンダー掛け）。ビニル床シートは仮敷きで巻きぐせを取り、接着剤をくし目ごてで塗ってべた張り→ローラー圧着。接合部は熱溶接工法（溝切り→溶接棒と同時溶融→冷却後に余盛りを削り平滑に）。",
    why: "ビニル床シートの防水性は「熱溶接」で決まります。継目を溝切りして溶接棒で溶かし込み、一体化させることで、水が継目から下地に入らなくなります。病院や厨房で漏水させないための核心技術です。下地の乾燥（28日）を守らないと、湿気で膨れます。",
    points: [
      "下地乾燥：モルタル14日・コンクリート28日以上",
      "熱溶接工法で継目を一体化（防水）",
      "べた張り＋ローラー圧着で空気だまりを防ぐ",
    ],
    diagrams: [
      {
        svg: SVG_DRY_PERIOD,
        caption: "床仕上げ前の下地の乾燥期間（19.2.3ほか）",
      },
      {
        svg: SVG_SHEET,
        caption: "ビニル床シートの熱溶接工法（19.2.3）",
      },
    ],
  },

  "19.2.4": {
    plainSummary:
      "張付け時・接着剤硬化前の室温が5℃以下になるおそれのある場合は施工を中止する（採暖等の養生を行う場合を除く）。",
  },

  "19.3.1": {
    plainSummary:
      "この節は織じゅうたん・タフテッドカーペット・タイルカーペットを用いる床仕上げ工事に適用する。これらは消防法の防炎性能を持ち防炎表示のあるものとする。下地は19.2.3による。",
    why: "カーペットは燃えやすいため、消防法で「防炎物品」であることが求められます。防炎表示（ラベル）の確認が必須です。",
    glossary: [
      {
        term: "タイルカーペット",
        meaning:
          "500mm角程度のタイル状カーペット。1枚ずつ交換でき、OAフロアと相性がよい。オフィスの定番。",
      },
    ],
  },

  "19.3.2": {
    plainSummary:
      "織じゅうたん（JIS L 4404、パイル糸は毛80%以上）、タフテッドカーペット（JIS L 4405、ナイロン）、タイルカーペット（JIS L 4406、特記がなければ500mm角・総厚6.5mm）。タイルカーペット用接着剤は粘着はく離（ピールアップ）形。",
    glossary: [
      {
        term: "粘着はく離形接着剤（ピールアップ）",
        meaning:
          "粘着力を保ったまま剥がせる接着剤。タイルカーペットの張り替え・配線変更に対応できる。",
      },
    ],
  },

  "19.3.3": {
    plainSummary:
      "工法はカーペットの種類に応じる（表19.3.2）。織じゅうたんはグリッパー工法（下敷き材を敷き周囲のグリッパーに引っ掛ける）、タイルカーペットは全面接着工法（粘着はく離形接着剤）。5℃以下では施工しない。グリッパー工法は伸張用工具で張力をかけて伸張。タイルカーペットは特記がなければ市松敷き。",
    why: "グリッパー工法は「引き伸ばして端を釘の付いた板（グリッパー）に引っ掛ける」方式で、下敷き材のクッションで足触りがよくなります。タイルカーペットの市松敷き（目の向きを交互）は、見た目のムラを抑え、1枚交換したときに目立ちにくくする工夫です。",
    points: [
      "織じゅうたん＝グリッパー工法、タイルカーペット＝全面接着",
      "タイルカーペットは市松敷きが基本",
    ],
    diagrams: [
      {
        svg: SVG_CARPET,
        caption: "カーペットの工法（19.3.3）",
      },
    ],
  },

  "19.4.1": {
    plainSummary:
      "この節は厚膜型塗床材（弾性ウレタン樹脂系・エポキシ樹脂系）・薄膜型塗床材（エポキシ樹脂系）を用いる床仕上げ工事に適用する。",
    glossary: [
      {
        term: "塗床（ぬりゆか）",
        meaning:
          "液状の樹脂を塗って継目のない床を作る仕上げ。厨房・工場・実験室など、耐薬品・耐水・衛生が求められる場所に使う。",
      },
    ],
  },

  "19.4.2": {
    plainSummary:
      "厚膜型は弾性ウレタン樹脂系（引張強さ6.5N/mm²以上等の品質）とエポキシ樹脂系（引張接着強さ1.0N/mm²以上・吸水性1%以下等）。薄膜型はエポキシ樹脂系。プライマー・トップコート等は製造所指定品（F☆☆☆☆）。",
    why: "ウレタン系は弾力があり足に優しく（体育施設等）、エポキシ系は硬く耐薬品性が高い（工場・実験室）。用途で使い分けます。",
  },

  "19.4.3": {
    plainSummary:
      "下地はモルタル14日以上・コンクリート28日以上乾燥（レイタンス・脆弱部を除去、表面強度を確認）。弾性ウレタン樹脂系塗床は平滑仕上げ・防滑仕上げ・つや消し仕上げ（プライマー→塗床材塗り→表面仕上げ）。1回の塗付け量は2kg/m²以下。塗床材は主剤と硬化剤を正確に計量して練り混ぜる。",
    why: "塗床は「2液を正確に計量して混ぜる」のが命。混合比を間違えると硬化不良になります。1回2kg/m²以下に分けるのは、厚塗りすると硬化熱や気泡でトラブルが出るためです。防滑仕上げは骨材を混ぜて滑りにくくします。",
    points: [
      "主剤と硬化剤を正確に計量して練り混ぜる",
      "1回の塗付け量は2kg/m²以下",
    ],
  },

  "19.4.4": {
    plainSummary:
      "気温5℃以下・湿度80%以上・換気不十分な場合は施工しない（採暖・換気を適切に行う場合を除く）。施工中は直射日光を避け換気・火気に注意。上塗り後は硬化まで吸湿・汚れを防ぐ養生。",
    why: "塗床の樹脂は湿気で白化（かぶり）したり硬化不良を起こします。湿度80%以上で施工しないのはそのため。溶剤系は火気厳禁で、換気も安全上必須です。",
  },

  "19.5.1": {
    plainSummary:
      "この節はフローリングを用いる床張り工事に適用する（体育館等の床は除く）。縁甲板張りは12章6節による。",
    glossary: [
      {
        term: "フローリング",
        meaning:
          "木質の床材。1枚の木の単層フローリングと、合板に化粧材を張った複合フローリングがある。",
      },
    ],
  },

  "19.5.2": {
    plainSummary:
      "フローリングはJAS 1073の工場塗装品（F☆☆☆☆等）。単層フローリング（フローリングボード・ブロック1等）と複合フローリング（天然木化粧）。種類は特記。",
  },

  "19.5.3": {
    plainSummary:
      "工法は釘留め工法（根太張り工法・直張り工法）と接着工法から特記。壁・幅木・框・敷居との取合いには板の伸縮に備えた隙間を設ける。5℃以下では施工しない。",
    why: "木は湿度で伸縮します。壁際にぴったり張ると、膨張時に床が突き上がる（浮く）ため、必ず伸縮の隙間（クリアランス）を設けます。これがフローリング施工の鉄則です。",
    points: [
      "壁際に伸縮の隙間を設ける（突き上げ防止）",
    ],
  },

  "19.5.4": {
    plainSummary:
      "根太張り工法は下張りせず根太の上に接着剤併用で釘留め（フローリングボード厚15mm等）。直張り工法は下張り用床板の上に接着剤併用で釘留め。継手が根太上に来るように、短辺の継手を揃えないよう敷き並べ、雄ざねの付け根から隠し釘留め。",
    why: "「隣り合う板の継手を揃えない（乱に配置）」のは、継手が一直線に並ぶと弱点・床鳴りになるため。雄ざねの付け根から斜めに打つ隠し釘は、釘頭が表に出ず美しく仕上がります。",
    glossary: [
      {
        term: "雄ざね（おざね）",
        meaning:
          "フローリングの側面に出た凸部。隣の板の凹部（雌ざね）にはめて連結する。",
      },
      {
        term: "根太（ねだ）",
        meaning: "床板を支える横木。この上にフローリングを張る。",
      },
    ],
  },

  "19.5.5": {
    plainSummary:
      "接着工法はコンクリート・モルタル下地に接着剤でフローリングを張る（フローリングボード厚8mm以上等）。裏面の不陸緩和材は特記がなければ合成樹脂発泡シート。接着剤を専用くしべらで全面塗布し、短辺の継手を揃えないよう平滑に張り込む。",
  },

  "19.5.6": {
    plainSummary:
      "施工後は吸湿・汚れを防ぎ、直射日光を避け、水が掛からないよう養生紙等で養生する。",
  },

  "19.6.1": {
    plainSummary: "この節は畳敷きに適用する。",
  },

  "19.6.2": {
    plainSummary:
      "畳は表19.6.1（畳床・畳表・畳へり等の構成でA〜D種）。衝撃緩和型畳（JIS A 5917の畳床）もある。種別は特記。",
    glossary: [
      {
        term: "畳床（たたみどこ）",
        meaning:
          "畳の芯材。稲わら製、建材（インシュレーションボード・発泡材）製などがある。",
      },
    ],
  },

  "19.6.3": {
    plainSummary:
      "畳ごしらえは畳割に正しく切り合わせ、へり幅は表2目とし、表の筋目通りよくたるまないよう針足寸法に合わせて縫い付ける。敷込みは敷居・畳寄せ等と段違い・隙間・不陸のないように行う。",
  },

  "19.7.1": {
    plainSummary:
      "この節はせっこうボード・その他ボード・合板を用いる天井・壁の仕上げ工事に適用する。",
    glossary: [
      {
        term: "せっこうボード",
        meaning:
          "石膏を芯にして両面を紙で覆ったボード。不燃・安価で加工しやすく、壁・天井下地の定番。",
      },
    ],
  },

  "19.7.2": {
    plainSummary:
      "せっこうボード・その他ボードは表19.7.1（防火材料の指定・認定品、F☆☆☆☆等）。合板は種類・厚さ等が特記（防火材料、湿潤箇所は1類接着）。小ねじは防錆処理品（湿気の多い箇所はステンレス製）。",
  },

  "19.7.3": {
    plainSummary:
      "壁のボードは縦張りで原則水平方向に継目を設けない。留付け間隔は表19.7.2（天井周辺部150mm・中間部200mm程度、壁周辺部200mm・中間部300mm程度）。せっこう系直張り用接着材による直張り工法（GLボンド工法）は、接着材を団子状に点付けし、盛上げ高さは仕上げ厚さの2倍以上、ボードを定規でたたきながら上下左右を調整して張る。",
    why: "壁を縦張りにして水平継目を作らないのは、水平の継目が目立ちやすくひび割れやすいため。直張り工法（GLボンド）は下地なしでコンクリートに直接ボードを張れる便利な工法で、団子状の接着材の高さでボードと躯体の隙間を調整します。",
    points: [
      "壁は縦張り・水平継目を作らない",
      "留付け間隔：天井周辺150mm・壁周辺200mm程度",
      "直張り工法の接着材盛上げは仕上げ厚の2倍以上",
    ],
    diagrams: [
      {
        svg: SVG_GB_DIRECT,
        caption: "せっこうボードの直張り工法（19.7.3）",
      },
    ],
  },

  "19.8.1": {
    plainSummary:
      "この節はモルタル面・コンクリート面・ボード面に施す壁紙張りに適用する。",
    glossary: [
      {
        term: "壁紙（クロス）",
        meaning:
          "壁・天井に張る仕上げ紙。ビニル製が主流。内装の最も一般的な仕上げ。",
      },
    ],
  },

  "19.8.2": {
    plainSummary:
      "壁紙はJIS A 6921（防火材料の指定・認定品、F☆☆☆☆）。接着剤はでん粉系（JIS A 6922、使用量は固形換算15〜60g/m²）。素地ごしらえのパテ・シーラーは壁紙専用品。下地の金物は防錆処理品。",
  },

  "19.8.3": {
    plainSummary:
      "モルタル面・コンクリート面・ボード面ごとに素地ごしらえの工程が定められている（表19.8.1〜、特記がなければB種）。乾燥→汚れ除去→吸込止め/下地調整→穴埋め・パテかい→研磨→（A種はパテしごき・研磨）。材料は研磨紙を除き壁紙専用品。",
    why: "壁紙は薄いので下地の凸凹・継目がそのまま出ます。パテで平滑にする素地ごしらえが仕上がりを決めます。パテやシーラーを壁紙専用品にするのは、壁紙の接着不良やアクの染み出しを防ぐためです。",
  },

  "19.9.1": {
    plainSummary: "この節は断熱材の打込み及び現場発泡工法に適用する。",
    glossary: [
      {
        term: "断熱材打込み工法",
        meaning:
          "型枠に断熱材を取り付けておき、コンクリートと一体に打ち込む工法。断熱材が躯体に密着する。",
      },
    ],
  },

  "19.9.2": {
    plainSummary:
      "火気・有害ガス等に対する安全衛生対策は関係法令に基づき適切に行う。",
    why: "現場発泡ウレタンは施工時に有害ガスが出ることがあり、火気にも弱いため、換気・防火の安全対策が重要です。",
  },

  "19.9.3": {
    plainSummary:
      "断熱材打込み工法。断熱材はJIS A 9521の発泡プラスチック断熱材を座付き釘等で型枠に取り付ける。配筋時は断熱材を損傷させない。コンクリート打込み時は振動機で断熱材を破損しないよう注意。型枠取外し後の損傷・付着不良は直ちに補修。開口部のモルタル詰め部・コーン撤去跡は断熱材を張るか充填する。",
    why: "打込み工法は断熱材が躯体と一体化するので断熱欠損が生じにくい反面、コーン跡や開口部の「熱橋（ヒートブリッジ）」になりやすい部分の補修が重要です。放置すると結露の原因になります。",
    points: [
      "コーン撤去跡・開口部のモルタル詰め部の断熱補修（熱橋対策）",
    ],
  },

  "19.9.4": {
    plainSummary:
      "断熱材現場発泡工法。断熱材は難燃性の吹付け硬質ウレタンフォーム（JIS A 9526、A種1またはA種1H）。吹付け厚さは確認ピンで確認（スラブ・壁面は5m²程度に1か所以上、柱・梁は1面に各1か所以上、確認ピンはそのまま存置）。",
    why: "現場発泡ウレタンは液を吹き付けて発泡させるので厚さがばらつきやすい。確認ピン（規定の長さのピン）を刺して厚さを目視確認するのが品質管理の要です。ピンをそのまま残すのは、後から厚さの証拠になるためです。",
    points: [
      "吹付け厚さは確認ピンで管理（5m²に1か所以上）",
    ],
    diagrams: [],
  },
};

export default ch19;
